import {
  calculatePosition, calculationPositionSchema, type CalculationPosition,
} from "@shared/calculation-template";
import type { LVSektion } from "@/components/project/leistungsverzeichnis-kalkulator";

export type ProjectCalculations = Record<string, CalculationPosition>;
export type ProjectSectionTitles = Record<string, string>;

/** Preserve these project-only fields in every full-replacement LV save. */
export function withProjectLvMetadata<T extends object>(
  state: T, calculations: ProjectCalculations, sectionTitles: ProjectSectionTitles,
): T & { calculations: ProjectCalculations; sectionTitles: ProjectSectionTitles } {
  return { ...state, calculations: structuredClone(calculations), sectionTitles: { ...sectionTitles } };
}

export function collectProjectCalculations(sections: LVSektion[]): ProjectCalculations {
  return Object.fromEntries(sections.flatMap((section) => section.positionen
    .filter((position) => !!position.calculation)
    .map((position) => [position.id, structuredClone(position.calculation!)])));
}

export function applyProjectPositionCalculation(
  sections: LVSektion[], positionId: string, draft: CalculationPosition,
): LVSektion[] {
  const section = sections.find((section) => section.positionen.some((position) => position.id === positionId));
  const position = section?.positionen.find((position) => position.id === positionId);
  if (!section || !position) throw new Error("Die LV-Position ist nicht mehr vorhanden.");
  const calculation = calculationPositionSchema.parse({ ...draft, section: section.id });
  const unitKey = (value: string) => value.trim().toLowerCase().replace(/²/g, "2").replace(/³/g, "3");
  if (!position.unit.trim() || /^ohne\s+einheit$/i.test(position.unit.trim()) ||
    unitKey(position.unit) !== unitKey(calculation.unit)) throw new Error("Die Kalkulationseinheit passt nicht zur LV-Position.");
  const ep = calculatePosition(calculation).price;
  if (!Number.isFinite(ep) || Math.abs(ep) > 1e9) throw new Error("Der berechnete Preis liegt außerhalb des zulässigen Bereichs.");
  return sections.map((group) => ({
    ...group, positionen: group.positionen.map((row) => row.id === positionId
      ? { ...row, ep, calculation: structuredClone(calculation) } : row),
  }));
}
